import { SeitenHinweis } from '../components/Hinweis.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import {
  faCircleExclamation,
  faLock,
} from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { GET_ZUGAENGE } from '../lib/graphql/queries.ts';
import { ZugangFormular } from '../components/ZugangFormular.tsx';
import type { EndpunktDefinitionInput } from '../gql/graphql.ts';
import { toast } from 'sonner';
import { useZugangBearbeiten } from '../hooks/useZugangBearbeiten.ts';
import type { KatalogAuswahl } from '../hooks/useKatalogAuswahl.ts';

const FEHLERTEXTE = {
  titelZuLang: 'domain.e2nApi.bearbeiten.titelZuLang',
  nichtEditierbar: 'domain.e2nApi.bearbeiten.nichtEditierbar.fehler',
  fehlgeschlagen: 'domain.e2nApi.bearbeiten.fehler',
} as const;

export function ZugangBearbeitenPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { uidZugang } = useParams();
  const { data, loading: zugaengeLadenLaeuft } = useQuery(GET_ZUGAENGE);
  const zugang =
    data?.zugaenge.__typename === 'ZugangQuerySuccess'
      ? data.zugaenge.zugaenge.find((zugang) => zugang.uid === uidZugang)
      : undefined;

  const { bearbeiten, loading: zugangBearbeitenLaeuft } = useZugangBearbeiten();

  if (zugaengeLadenLaeuft) {
    return null;
  }

  if (!zugang) {
    return (
      <SeitenHinweis
        icon={faCircleExclamation}
        art="fehler"
        titel={t('domain.e2nApi.fehler.laden.titel')}
        beschreibung={t('domain.e2nApi.fehler.laden.beschreibung')}
      />
    );
  }

  if (zugang.partnerTemplate) {
    return (
      <SeitenHinweis
        icon={faLock}
        titel={t('domain.e2nApi.bearbeiten.nichtEditierbar.titel')}
        beschreibung={t(
          'domain.e2nApi.bearbeiten.nichtEditierbar.beschreibung',
        )}
      />
    );
  }

  async function handleBearbeiten(
    uidZugang: string,
    titel: string,
    definitionen: EndpunktDefinitionInput[],
  ): Promise<void> {
    const ergebnis = await bearbeiten(uidZugang, titel, definitionen);

    if (ergebnis === 'erfolg') {
      toast.success(t('domain.e2nApi.bearbeiten.erfolg'));
      navigate('..');
      return;
    }

    if (ergebnis === 'endpunkteFehlgeschlagen') {
      toast.warning(t('domain.e2nApi.bearbeiten.endpunkteFehler'));
      return;
    }

    toast.error(t(FEHLERTEXTE[ergebnis]));
  }

  return (
    <ZugangFormular
      titel={t('domain.e2nApi.bearbeiten.title')}
      key={zugang.uid}
      zugangUid={zugang.uid}
      startAuswahl={toAuswahl(zugang.endpunktDefinitionen)}
      startTitel={zugang.beschreibung}
      laeuft={zugangBearbeitenLaeuft}
      absendenText={t('domain.e2nApi.karte.uebernehmen')}
      onAbsenden={(titel, definitionen) =>
        handleBearbeiten(zugang.uid, titel, definitionen)
      }
    />
  );
}

function toAuswahl(
  definitionen: { endpunktName: string; attribute: string[] }[],
): KatalogAuswahl {
  return Object.fromEntries(
    definitionen.map((definition) => [
      definition.endpunktName,
      definition.attribute,
    ]),
  );
}
